import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, TextInput, NumberInput, ActionIcon, FileInput, Loader } from '@mantine/core';
import { IconPlus, IconTrash, IconReceiptTax, IconUpload, IconExternalLink } from '@tabler/icons-react';
import { PaymentRetentionItem } from '@turizi/shared';
import { MoneyNumberInput } from '@/components/snippets';
import { formatPrice } from '@/lib/format-utils';
import {
    ARGENTINA_IIBB_JURISDICTIONS,
    DEFAULT_RETENTION_PERCENTAGE_BY_TYPE,
    RETENTION_PERCENTAGE_PRESETS,
    calculateRetentionAmount,
} from '@/lib/argentina-retentions';
import api from '@/services/api-service';
import { notify } from '@/lib/notify';

interface PaymentRetentionsSectionProps {
    retentions: PaymentRetentionItem[];
    totalAmount?: number;
    currency?: string;
    onChange: (retentions: PaymentRetentionItem[], retainedAmount: number) => void;
    compact?: boolean;
}

const RETENTION_TYPE_KEYS = ['ganancias', 'iibb', 'iva', 'suss', 'other'] as const;

export default function PaymentRetentionsSection({
    retentions = [],
    totalAmount = 0,
    currency = 'ARS',
    onChange,
    compact = false,
}: PaymentRetentionsSectionProps) {
    const { t } = useTranslation();
    const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);

    const totalRetained = retentions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const netCash = Math.max(0, Number(totalAmount || 0) - totalRetained);

    const emitChange = (nextList: PaymentRetentionItem[]) => {
        const nextTotal = nextList.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        onChange(nextList, nextTotal);
    };

    const handleAddRetention = () => {
        const defaultPct = DEFAULT_RETENTION_PERCENTAGE_BY_TYPE.iibb;
        const defaultAmt = calculateRetentionAmount(Number(totalAmount || 0), defaultPct);
        const newItem: PaymentRetentionItem = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            type: 'iibb',
            jurisdiction: 'CABA (AGIP)',
            certificate_number: '',
            percentage: defaultPct,
            amount: defaultAmt,
            attachment_url: null,
        };
        emitChange([...retentions, newItem]);
    };

    const handleUpdateItem = (index: number, patch: Partial<PaymentRetentionItem>) => {
        const next = retentions.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
        emitChange(next);
    };

    const handleTypeChange = (index: number, newType: string) => {
        const defaultPct = DEFAULT_RETENTION_PERCENTAGE_BY_TYPE[newType] ?? 0;
        const nextAmount = defaultPct > 0
            ? calculateRetentionAmount(Number(totalAmount || 0), defaultPct)
            : retentions[index]?.amount || 0;

        handleUpdateItem(index, {
            type: newType,
            percentage: defaultPct,
            jurisdiction: newType === 'iibb' ? (retentions[index]?.jurisdiction || 'CABA (AGIP)') : '',
            amount: nextAmount,
        });
    };

    const handlePercentageChange = (index: number, val: number | string) => {
        const pct = Number(val) || 0;
        const nextAmount = pct > 0 && Number(totalAmount || 0) > 0
            ? calculateRetentionAmount(Number(totalAmount || 0), pct)
            : retentions[index]?.amount || 0;
        handleUpdateItem(index, {
            percentage: pct,
            amount: nextAmount,
        });
    };

    const handleJurisdictionSelect = (index: number, jurName: string | null) => {
        if (!jurName) {
            handleUpdateItem(index, { jurisdiction: '' });
            return;
        }
        const found = ARGENTINA_IIBB_JURISDICTIONS.find((j) => j.name === jurName);
        if (found) {
            const nextAmount = Number(totalAmount || 0) > 0
                ? calculateRetentionAmount(Number(totalAmount || 0), found.defaultRate)
                : retentions[index]?.amount || 0;
            handleUpdateItem(index, {
                jurisdiction: found.name,
                percentage: found.defaultRate,
                amount: nextAmount,
            });
        } else {
            handleUpdateItem(index, { jurisdiction: jurName });
        }
    };

    const handleRemoveItem = (index: number) => {
        const next = retentions.filter((_, idx) => idx !== index);
        emitChange(next);
    };

    const handleUploadCertificate = async (index: number, file: File | null) => {
        if (!file) return;
        setUploadingIdx(index);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await api.upload<{ url: string; key: string }>('/payments/upload-attachment', formData);
            if (res.error) {
                notify.error(t('error'), typeof res.error === 'string' ? res.error : t('error'));
            } else if (res.data?.url) {
                handleUpdateItem(index, { attachment_url: res.data.url });
            }
        } catch {
            notify.error(t('error'), t('error'));
        } finally {
            setUploadingIdx(null);
        }
    };

    const typeOptions = RETENTION_TYPE_KEYS.map((key) => ({
        value: key,
        label: t(`retention_types.${key}`),
    }));

    const jurisdictionOptions = ARGENTINA_IIBB_JURISDICTIONS.map((j) => ({
        value: j.name,
        label: `${j.name} (${j.defaultRate}%)`,
    }));

    return (
        <div className={compact ? 'space-y-2.5' : 'space-y-3'}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <IconReceiptTax size={15} className="text-amber-600" />
                    <span>{t('retentions')}</span>
                </div>
                <Button
                    size="compact-xs"
                    variant="light"
                    color="amber"
                    leftSection={<IconPlus size={13} />}
                    onClick={handleAddRetention}
                >
                    {t('add_retention')}
                </Button>
            </div>

            {retentions.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-1">
                    {t('no_retentions')}
                </div>
            ) : (
                <div className="space-y-2.5">
                    {retentions.map((item, index) => {
                        const presets = RETENTION_PERCENTAGE_PRESETS[item.type] || RETENTION_PERCENTAGE_PRESETS.other;
                        return (
                            <div
                                key={item.id || index}
                                className="p-3 rounded-xl bg-amber-50/40 border border-amber-200/70 space-y-2.5"
                            >
                                {/* Row 1: Type, Percentage (%), and Wide Amount Input */}
                                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-end">
                                    <div className="sm:col-span-5">
                                        <Select
                                            size="sm"
                                            label={t('retention_type')}
                                            data={typeOptions}
                                            value={item.type}
                                            onChange={(val) => handleTypeChange(index, val || 'other')}
                                            allowDeselect={false}
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <NumberInput
                                            size="sm"
                                            label={t('retention_percentage')}
                                            value={item.percentage ?? ''}
                                            onChange={(val) => handlePercentageChange(index, val)}
                                            min={0}
                                            max={100}
                                            decimalScale={2}
                                            suffix="%"
                                            hideControls
                                        />
                                    </div>
                                    <div className="sm:col-span-5">
                                        <MoneyNumberInput
                                            size="sm"
                                            label={t('amount')}
                                            amount={item.amount}
                                            onChange={(val) => handleUpdateItem(index, { amount: Number(val || 0) })}
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Jurisdiction / Standard Regime Preset & Certificate Number */}
                                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-end">
                                    <div className="sm:col-span-6">
                                        {item.type === 'iibb' ? (
                                            <Select
                                                size="xs"
                                                label={t('retention_jurisdiction')}
                                                placeholder={t('retention_jurisdiction_placeholder')}
                                                data={jurisdictionOptions}
                                                value={item.jurisdiction || null}
                                                onChange={(val) => handleJurisdictionSelect(index, val)}
                                                searchable
                                                clearable
                                            />
                                        ) : (
                                            <Select
                                                size="xs"
                                                label={t('retention_preset')}
                                                data={presets.map((p) => ({
                                                    value: String(p.percentage),
                                                    label: p.label,
                                                }))}
                                                value={item.percentage !== undefined && item.percentage !== null ? String(item.percentage) : null}
                                                onChange={(val) => {
                                                    if (val) handlePercentageChange(index, Number(val));
                                                }}
                                                clearable
                                            />
                                        )}
                                    </div>
                                    <div className="sm:col-span-6">
                                        <TextInput
                                            size="xs"
                                            label={t('retention_certificate_number')}
                                            placeholder={t('retention_certificate_placeholder')}
                                            value={item.certificate_number || ''}
                                            onChange={(e) => handleUpdateItem(index, { certificate_number: e.currentTarget.value })}
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Certificate Attachment & Remove Action */}
                                <div className="flex items-center justify-between gap-2 pt-1 border-t border-amber-200/40">
                                    <div className="flex items-center gap-2">
                                        {uploadingIdx === index ? (
                                            <Loader size="xs" />
                                        ) : item.attachment_url ? (
                                            <div className="flex items-center gap-1.5">
                                                <a
                                                    href={item.attachment_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline bg-white px-2 py-1 rounded border border-blue-200"
                                                >
                                                    <IconExternalLink size={12} />
                                                    <span>{t('view_certificate')}</span>
                                                </a>
                                                <ActionIcon
                                                    size="xs"
                                                    color="gray"
                                                    variant="subtle"
                                                    onClick={() => handleUpdateItem(index, { attachment_url: null })}
                                                >
                                                    <IconTrash size={12} />
                                                </ActionIcon>
                                            </div>
                                        ) : (
                                            <FileInput
                                                size="xs"
                                                placeholder={t('upload_certificate')}
                                                accept="image/png,image/jpeg,application/pdf"
                                                leftSection={<IconUpload size={13} />}
                                                onChange={(file) => handleUploadCertificate(index, file)}
                                            />
                                        )}
                                    </div>

                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        size="md"
                                        onClick={() => handleRemoveItem(index)}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </div>
                            </div>
                        );
                    })}

                    {totalRetained > 0 && (
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1">
                            <div className="flex items-center justify-between text-slate-600">
                                <span>{t('total_settled_amount')}:</span>
                                <span className="font-bold text-slate-800">
                                    ${formatPrice(Number(totalAmount || 0))} {currency}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-amber-700">
                                <span>{t('retained_amount')}:</span>
                                <span className="font-bold">
                                    -${formatPrice(totalRetained)} {currency}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-emerald-700 pt-1 border-t border-slate-200 font-bold">
                                <span>{t('net_cash_amount')}:</span>
                                <span>
                                    ${formatPrice(netCash)} {currency}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
